import { useEffect, useRef, useState } from "react";
import { api, day, money } from "../../api.js";
import { decodeCp1251 } from "../../../../lib/pn/cp1251.js";
import { PAYROLL_FORMS, readXml } from "../../../../lib/payroll/form.js";
import formCssUrl from "../../../../lib/vat/form.css?url";
import { ScaledForm } from "../pn/PnForm.jsx";
import ReportSign, { b64ToBytes } from "../ReportSign.jsx";
import "../vat/vat.css";
import "./payroll.css";

// «Звітність» → «Уточнити» для Податкового розрахунку ПДФО/ВЗ/ЄСВ: рядки поданих 4ДФ і Д1 — змініть суми або позначте рядок
// на вилучення. Уточнюючий містить лише ці рядки: помилковий — з ознакою «1», правильний — з «0» (lib/payroll/ur.js).
// Далі — бланки, «Я переглянув(-ла)» і подання спільним потоком ReportSign
const A4 = 794;
const MONTHS = ["січень", "лютий", "березень", "квітень", "травень", "червень", "липень", "серпень", "вересень", "жовтень", "листопад", "грудень"];
const monthName = (p) => (p ? `${MONTHS[Number(p.slice(5, 7)) - 1]} ${p.slice(0, 4)}` : "");
const NAMES = { main: "Розрахунок", d1: "Д1 (ЄСВ)", d4: "4ДФ (ПДФО, ВЗ)" };

export default function PayrollUr({ companyId, company, draftId, version }) {
  const base = `/api/companies/${companyId}/payroll-ur/drafts/${draftId}`;
  const canEdit = ["preparer", "submitter", "owner"].includes(company?.role);
  const [data, setData] = useState(null), [err, setErr] = useState(""), [busy, setBusy] = useState(null);
  const [edits, setEdits] = useState({ d4: {}, d1: {} }), [tab, setTab] = useState("main");
  const timer = useRef(null), ver = useRef(null);
  useEffect(() => { if (data) ver.current = data.draft.version; }, [data]);
  const load = async () => { const x = await api(base); setData(x); setEdits({ d4: {}, d1: {}, ...x.draft.doc.edits }); };
  useEffect(() => { load().catch((e) => setErr(e.message)); }, [draftId]);
  const save = (next) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setBusy("save"); setErr("");
      const clean = Object.fromEntries(["d4", "d1"].map((k) => [k, Object.fromEntries(Object.entries(next[k] || {}).map(([n, e]) => [n, e.remove ? { remove: true }
        : Object.fromEntries(Object.entries(e).filter(([, v]) => v !== "" && Number.isFinite(Number(String(v).replace(",", ".")))).map(([c, v]) => [c, Number(String(v).replace(",", "."))]))]))]));
      try { const x = await api(base, { method: "PUT", body: { version: ver.current, edits: clean } }); setData((d) => ({ ...d, ...x })); }
      catch (e) { setErr(e.message); if (e.status === 409) await load(); }
      finally { setBusy(null); }
    }, 700);
  };
  const setCell = (kind, n, col, v) => { const next = { ...edits, [kind]: { ...edits[kind], [n]: { ...(edits[kind][n] || {}), [col]: v } } }; setEdits(next); save(next); };
  const toggleRemove = (kind, n) => { const cur = edits[kind][n]; const next = { ...edits, [kind]: { ...edits[kind], [n]: cur?.remove ? {} : { remove: true } } }; setEdits(next); save(next); };
  const reset = (kind, n) => { const k = { ...edits[kind] }; delete k[n]; const next = { ...edits, [kind]: k }; setEdits(next); save(next); };

  if (err && !data) return <p className="error">{err}</p>;
  if (!data) return <p className="muted" role="status">Завантаження…</p>;
  const { draft, source, result, locked, columns } = data, doc = draft.doc;
  const reviewed = draft.reviewed?.sha256 && draft.reviewed.sha256 === data.sha256 && draft.reviewed.version === draft.version;
  const review = async () => { setBusy("review"); setErr(""); try { await api(`${base}/review`, { method: "POST", body: { sha256: data.sha256, version: draft.version } }); await load(); } catch (e) { setErr(e.message); } finally { setBusy(null); } };
  const editable = canEdit && !locked;
  const d = result.diff;

  const table = (kind) => (
    <div className="table-wrap" tabIndex={0} role="region" aria-label={NAMES[kind]}>
      <table className="table-static vat-ur-table">
        <thead><tr><th>Працівник</th>{kind === "d4" ? <th>Ознака доходу</th> : <th>Місяць</th>}{Object.values(columns[kind]).map((t) => <th key={t} className="num">{t}</th>)}<th /></tr></thead>
        <tbody>{source[kind].map((r) => {
          const e = edits[kind][r.n] || {}, removed = Boolean(e.remove), changed = removed || Object.keys(e).length > 0;
          return (
            <tr key={r.n} className={changed ? "changed" : ""}>
              <td className="wrap-cell"><b>{r.name || "—"}</b><div className="muted small">{r.rnokpp}</div></td>
              <td>{kind === "d4" ? r.income : r.month}</td>
              {Object.keys(columns[kind]).map((c) => (
                <td key={c} className="num">{removed ? <s className="muted">{money(r.values[c])}</s> : editable
                  ? <span className="vat-ur-cell"><input className="input" inputMode="decimal" value={e[c] ?? String(r.values[c])} onChange={(ev) => setCell(kind, r.n, c, ev.target.value)}
                      aria-label={`${r.name || r.rnokpp}: ${columns[kind][c]}`} /></span>
                  : money(e[c] ?? r.values[c])}
                  {!removed && e[c] != null && Number(String(e[c]).replace(",", ".")) !== r.values[c] && <div className="muted small">було {money(r.values[c])}</div>}</td>))}
              <td>{editable && <span className="row">
                <button type="button" className="linkish small" onClick={() => toggleRemove(kind, r.n)}>{removed ? "Повернути" : "Вилучити рядок"}</button>
                {changed && !removed && <button type="button" className="linkish small" onClick={() => reset(kind, r.n)} title="Повернути подані суми">↺</button>}</span>}</td>
            </tr>);
        })}</tbody>
      </table>
    </div>);

  return (
    <div className="payroll-ur">
      <p><a href={`#/c/${companyId}/reports`}>← Звітність</a></p>
      <div className="section-head"><div>
        <h2>Уточнюючий Податковий розрахунок за {monthName(doc.period)}</h2>
        <p className="status">На основі поданого {doc.source.cdoc} (прийнято {day(doc.source.dget)}), № розрахунку {doc.hnum}. Змініть суми рядків з помилкою або вилучіть зайвий рядок —
          у розрахунок підуть лише ці рядки: помилковий з ознакою «1», правильний з ознакою «0». Рядок «Всього» додатків у уточнюючому не заповнюється.</p>
      </div></div>

      <div className="card"><h3>4ДФ — доходи, ПДФО, військовий збір</h3>{table("d4")}</div>
      <div className="card"><h3>Д1 — нарахування ЄСВ</h3>{table("d1")}{busy === "save" && <p className="muted small">зберігаємо…</p>}</div>

      <div className="card">
        <h3>Підсумок уточнення</h3>
        <ul className="small">
          <li>Дохід: {d.income > 0 ? "+" : ""}{money(d.income)} · ПДФО: {d.pdfo > 0 ? "+" : ""}{money(d.pdfo)} · ВЗ: {d.vz > 0 ? "+" : ""}{money(d.vz)}</li>
          <li>Зарплата (Д1): {d.salary > 0 ? "+" : ""}{money(d.salary)} · ЄСВ: {d.esv > 0 ? "+" : ""}{money(d.esv)}</li>
          {result.fine > 0 && <li>Штраф 3 % (ПДФО і ВЗ): <b>{money(result.fine)} грн</b></li>}
          {result.pay > 0 && <li><b>Сплатіть до подання: {money(result.pay)} грн</b> (ПДФО, ВЗ і штраф — на рахунки ПДФО й ВЗ; ЄСВ — на рахунок ЄСВ)</li>}
        </ul>
        {result.warnings.map((w, i) => <p key={i} className="notice small">{w}</p>)}
        {result.errors.map((e, i) => <p key={i} className="error small" role="alert">{e}</p>)}
        {err && <p className="error small" role="alert">{err}</p>}
      </div>

      {data.xml && (
        <div className="card">
          <h3>Бланки</h3>
          <div className="tabs" role="group" aria-label="Документи комплекту">{Object.keys(data.xml).map((k) => (
            <button key={k} type="button" className={tab === k ? "tab active" : "tab"} aria-pressed={tab === k} onClick={() => setTab(k)}>{NAMES[k]}</button>))}</div>
          {data.xml[tab] && <ScaledForm html={(PAYROLL_FORMS[tab] || PAYROLL_FORMS.main)(data.xml[tab], { cssHref: new URL(formCssUrl, location.href).href })} width={A4} title={NAMES[tab]} />}
          {reviewed ? <p className="pn-ok">✓ Переглянуто {day(draft.reviewed.at)}. Будь-яка зміна скине підтвердження.</p>
            : editable && <button type="button" className="btn" disabled={Boolean(busy)} onClick={review}>Я переглянув(-ла) уточнюючий розрахунок</button>}
        </div>
      )}
      {(reviewed || locked) && <UrSign companyId={companyId} company={company} draftId={draftId} version={version} />}
    </div>
  );
}

function UrSign({ companyId, company, draftId, version }) {
  const blanks = (sub) => {
    const cssHref = new URL(formCssUrl, location.href).href;
    const xml = Object.fromEntries(sub.docs.map((x) => [x.kind, decodeCp1251(b64ToBytes(x.dpsXml))]));
    const main = readXml(xml.main);
    return { tabs: sub.docs.map((x) => ({ key: x.kind, label: NAMES[x.kind] || x.cdoc, html: (PAYROLL_FORMS[x.kind] || PAYROLL_FORMS.main)(xml[x.kind], { cssHref }) })),
      signer: { kbos: main.tag("HKBOS"), bos: main.tag("HBOS") } };
  };
  return <ReportSign companyId={companyId} company={company} draftId={draftId} apiKey="payroll-ur" blanks={blanks} what="уточнюючий розрахунок" version={version} />;
}
